import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
const PRESERVED_REQUEST_PARAMETER_KEYS = ['preview_key', 'preview_theme_id'];

function getPreservedRequestParameters() {
  const searchParams = new URLSearchParams(window.location.search);
  const params = [];

  for (const key of PRESERVED_REQUEST_PARAMETER_KEYS) {
    const value = searchParams.get(key);
    if (value) params.push(`${key}=${encodeURIComponent(value)}`);
  }

  if (searchParams.has('preview_key') && !searchParams.has('preview_theme_id')) {
    const themeId = window.Shopify?.theme?.id?.toString();
    if (themeId) params.push(`preview_theme_id=${encodeURIComponent(themeId)}`);
  }

  return params;
}

export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  #adaptiveMediaQuery = window.matchMedia('(max-width: 749px)');

  /** @type {{ id: string, options: string[] }[]} */
  #eligibleVariants = [];

  connectedCallback() {
    super.connectedCallback();
    this.#eligibleVariants = this.#readVariantGate();
    let needsInitialSelectionSync = this.#collectFieldsetState();

    if (this.#eligibleVariants.length) {
      needsInitialSelectionSync = this.#syncSelectionToEligibleVariant() || needsInitialSelectionSync;
      this.#applyVariantGate();
    }

    this.addEventListener('change', this.variantChanged.bind(this));
    this.addEventListener('click', this.#handleAdaptivePickerClick);
    this.#resizeObserver.observe(this);

    if (needsInitialSelectionSync && this.selectedOption) {
      queueMicrotask(() => this.#selectionEventTarget()?.dispatchEvent(new Event('change', { bubbles: true })));
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.removeEventListener('click', this.#handleAdaptivePickerClick);
  }

  #collectFieldsetState() {
    this.#radios = [];
    this.#checkedIndices = [];
    let needsInitialSelectionSync = false;
    const fieldsets = Array.from(this.querySelectorAll('fieldset[data-fieldset-index]'));
    const selectedVariantOptions = this.#selectedVariantOptions();

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      const fieldsetIndex = Number.parseInt(fieldset.dataset.fieldsetIndex || '', 10);
      if (Number.isNaN(fieldsetIndex)) return;
      this.#radios[fieldsetIndex] = radios;

      let initialCheckedIndex = selectedVariantOptions[fieldsetIndex]
        ? radios.findIndex((radio) => radio.value === selectedVariantOptions[fieldsetIndex])
        : -1;
      if (initialCheckedIndex === -1) {
        initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      }
      if (initialCheckedIndex === -1) {
        initialCheckedIndex = radios.findIndex(
          (radio) => radio.dataset.optionAvailable !== 'false' && radio.getAttribute('aria-disabled') !== 'true'
        );
        if (initialCheckedIndex !== -1) {
          radios[initialCheckedIndex].dataset.currentChecked = 'true';
          needsInitialSelectionSync = true;
        }
      }
      if (initialCheckedIndex !== -1) {
        radios.forEach((radio, index) => {
          const isCurrent = index === initialCheckedIndex;
          radio.checked = isCurrent;
          radio.dataset.currentChecked = String(isCurrent);
          radio.dataset.previousChecked = 'false';
        });
        this.#checkedIndices[fieldsetIndex] = [initialCheckedIndex];
      }
    });

    return needsInitialSelectionSync;
  }

  /**
   * @returns {string[]}
   */
  #selectedVariantOptions() {
    const script = this.querySelector('script[type="application/json"]:not([data-ab-variant-gate])');
    const text = script?.textContent?.trim();
    if (!text) return [];

    try {
      const variant = JSON.parse(text);
      return Array.isArray(variant?.options) ? variant.options.map((option) => String(option)) : [];
    } catch (error) {
      console.warn('Unable to parse selected variant options', error);
      return [];
    }
  }

  #readVariantGate() {
    const script = this.querySelector('script[data-ab-variant-gate]');
    const text = script?.textContent?.trim();
    if (!text) return [];

    try {
      let gate = JSON.parse(text);
      if (typeof gate === 'string') gate = JSON.parse(gate);
      const variants = Array.isArray(gate?.eligibleVariants) ? gate.eligibleVariants : [];

      return variants
        .map((variant) => ({
          id: String(variant?.id || '').replace('gid://shopify/ProductVariant/', ''),
          options: Array.isArray(variant?.options) ? variant.options.map((option) => String(option)) : [],
        }))
        .filter((variant) => variant.id && variant.options.length);
    } catch (error) {
      console.warn('Unable to parse product page variant gate', error);
      return [];
    }
  }

  /**
   * @returns {string[]}
   */
  #currentOptionValues() {
    const selectedValues = [];

    this.#radios.forEach((radios, fieldsetIndex) => {
      selectedValues[fieldsetIndex] = radios.find((radio) => radio.checked)?.value || '';
    });

    const selects = this.querySelectorAll('select[data-fieldset-index]');
    for (const select of selects) {
      if (!(select instanceof HTMLSelectElement)) continue;
      const fieldsetIndex = Number.parseInt(select.dataset.fieldsetIndex || '', 10);
      if (!Number.isNaN(fieldsetIndex)) {
        selectedValues[fieldsetIndex] = select.value || '';
      }
    }

    return selectedValues;
  }

  /**
   * @param {{ id: string, options: string[] }} variant
   * @param {string[]} selectedValues
   */
  #variantMatchesSelection(variant, selectedValues) {
    return variant.options.every((option, index) => selectedValues[index] === option);
  }

  #selectedEligibleVariant() {
    const selectedValues = this.#currentOptionValues();
    return this.#eligibleVariants.find((variant) => this.#variantMatchesSelection(variant, selectedValues));
  }

  /**
   * @param {number} fieldsetIndex
   * @param {string} value
   */
  #optionValueHasEligibleVariant(fieldsetIndex, value) {
    return this.#eligibleVariants.some((variant) => variant.options[fieldsetIndex] === value);
  }

  #applyVariantGate() {
    if (!this.#eligibleVariants.length) return;

    this.#radios.forEach((radios, fieldsetIndex) => {
      for (const radio of radios) {
        this.#setInputGateState(radio, this.#optionValueHasEligibleVariant(fieldsetIndex, radio.value));
      }
    });

    const selects = this.querySelectorAll('select[data-fieldset-index]');
    for (const select of selects) {
      if (!(select instanceof HTMLSelectElement)) continue;
      const fieldsetIndex = Number.parseInt(select.dataset.fieldsetIndex || '', 10);
      if (Number.isNaN(fieldsetIndex)) continue;

      for (const option of select.options) {
        const isEligible = this.#optionValueHasEligibleVariant(fieldsetIndex, option.value);
        option.disabled = !isEligible;
        option.hidden = !isEligible;
        option.dataset.variantEligible = String(isEligible);
      }
    }
  }

  /**
   * @param {HTMLInputElement} input
   * @param {boolean} isEligible
   */
  #setInputGateState(input, isEligible) {
    input.disabled = !isEligible;
    input.hidden = !isEligible;
    input.dataset.variantEligible = String(isEligible);
    if (isEligible) {
      input.removeAttribute('aria-disabled');
    } else {
      input.setAttribute('aria-disabled', 'true');
    }

    const label = input.closest('label');
    if (label instanceof HTMLElement) {
      label.hidden = !isEligible;
      label.dataset.variantEligible = String(isEligible);
    }
  }

  /**
   * @param {{ fieldsetIndex: number, value: string } | undefined} preferredOption
   */
  #bestEligibleVariant(preferredOption = undefined) {
    const selectedValues = this.#currentOptionValues();
    let bestVariant = this.#eligibleVariants[0];
    let bestScore = Number.NEGATIVE_INFINITY;

    for (const variant of this.#eligibleVariants) {
      let score = 0;
      variant.options.forEach((option, index) => {
        if (selectedValues[index] && selectedValues[index] === option) score += 1;
      });
      if (preferredOption && variant.options[preferredOption.fieldsetIndex] === preferredOption.value) {
        score += 100;
      }
      if (score > bestScore) {
        bestScore = score;
        bestVariant = variant;
      }
    }

    return bestVariant;
  }

  /**
   * @param {{ fieldsetIndex: number, value: string } | undefined} preferredOption
   */
  #syncSelectionToEligibleVariant(preferredOption = undefined) {
    if (!this.#eligibleVariants.length || this.#selectedEligibleVariant()) return false;

    const targetVariant = this.#bestEligibleVariant(preferredOption);
    if (!targetVariant) return false;

    let changed = false;
    targetVariant.options.forEach((optionValue, fieldsetIndex) => {
      if (this.#setSelectedFieldsetValue(fieldsetIndex, optionValue)) {
        changed = true;
      }
    });

    if (changed) this.updateVariantPickerCss();
    return changed;
  }

  /**
   * @param {number} fieldsetIndex
   * @param {string} value
   */
  #setSelectedFieldsetValue(fieldsetIndex, value) {
    let changed = false;
    const radios = this.#radios[fieldsetIndex] || [];
    const targetRadioIndex = radios.findIndex((radio) => radio.value === value);

    if (targetRadioIndex !== -1) {
      radios.forEach((radio, index) => {
        const isTarget = index === targetRadioIndex;
        if (radio.checked !== isTarget) changed = true;
        radio.checked = isTarget;
        radio.dataset.currentChecked = String(isTarget);
        radio.dataset.previousChecked = 'false';
      });
      this.#checkedIndices[fieldsetIndex] = [targetRadioIndex];
      this.#updateAdaptivePickerSummary(radios[targetRadioIndex]);
    }

    const select = this.querySelector(`select[data-fieldset-index="${fieldsetIndex}"]`);
    if (select instanceof HTMLSelectElement && select.value !== value) {
      select.value = value;
      this.updateSelectedOption(select);
      changed = true;
    }

    return changed;
  }

  #selectionEventTarget() {
    const selectedInput = this.querySelector('fieldset input:checked');
    if (selectedInput instanceof HTMLInputElement) return selectedInput;

    const select = this.querySelector('select');
    if (select instanceof HTMLSelectElement) return select;

    return undefined;
  }

  /**
   * @param {HTMLElement} selectedOption
   */
  #preferredOptionFromSelectedOption(selectedOption) {
    if (selectedOption instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(selectedOption.dataset.fieldsetIndex || '', 10);
      if (!Number.isNaN(fieldsetIndex)) return { fieldsetIndex, value: selectedOption.value };
    }

    if (selectedOption instanceof HTMLOptionElement) {
      const fieldsetIndex = Number.parseInt(selectedOption.parentElement?.dataset.fieldsetIndex || '', 10);
      if (!Number.isNaN(fieldsetIndex)) return { fieldsetIndex, value: selectedOption.value };
    }

    return undefined;
  }

  /**
   * @param {HTMLElement} selectedOption
   */
  #isUnavailableCombination(selectedOption) {
    if (selectedOption instanceof HTMLInputElement) {
      return selectedOption.dataset.optionAvailable === 'false' || selectedOption.getAttribute('aria-disabled') === 'true';
    }

    if (selectedOption instanceof HTMLOptionElement) {
      return (
        selectedOption.dataset.optionAvailable === 'false' ||
        selectedOption.getAttribute('aria-disabled') === 'true' ||
        selectedOption.disabled
      );
    }

    return false;
  }

  /**
   * @param {HTMLElement} selectedOption
   */
  #restoreSelectedOptionState(selectedOption) {
    if (selectedOption instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(selectedOption.dataset.fieldsetIndex || '', 10);
      if (Number.isNaN(fieldsetIndex)) return;

      const currentIndex = this.#checkedIndices[fieldsetIndex]?.[0];
      const radios = this.#radios[fieldsetIndex] || [];
      radios.forEach((radio, index) => {
        const isCurrent = index === currentIndex;
        radio.checked = isCurrent;
        radio.dataset.currentChecked = String(isCurrent);
        radio.dataset.previousChecked = 'false';
      });
      this.updateFieldsetCss(fieldsetIndex);
      return;
    }

    if (selectedOption instanceof HTMLOptionElement) {
      const select = selectedOption.parentElement;
      if (!(select instanceof HTMLSelectElement)) return;

      const selectedAttributeOption = Array.from(select.options).find((option) => option.hasAttribute('selected'));
      if (selectedAttributeOption) select.value = selectedAttributeOption.value;
    }
  }

  #handleAdaptivePickerClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const toggle = event.target.closest('[data-adaptive-toggle]');
    if (!(toggle instanceof HTMLButtonElement) || !this.contains(toggle)) return;

    const fieldset = toggle.closest('.variant-option--adaptive-compact');
    if (!(fieldset instanceof HTMLFieldSetElement)) return;

    this.#setAdaptivePickerExpanded(fieldset, fieldset.dataset.adaptiveExpanded !== 'true');
  };

  /**
   * @param {HTMLFieldSetElement} fieldset
   * @param {boolean} expanded
   */
  #setAdaptivePickerExpanded(fieldset, expanded) {
    fieldset.dataset.adaptiveExpanded = expanded ? 'true' : 'false';

    const toggles = fieldset.querySelectorAll('[data-adaptive-toggle]');
    for (const toggle of toggles) {
      toggle.setAttribute('aria-expanded', String(expanded));
    }

    if (!expanded || !this.#adaptiveMediaQuery.matches) return;

    requestAnimationFrame(() => {
      fieldset.querySelector('input:checked')?.closest('.ab-adaptive-variant-card')?.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
      });
    });
  }

  /**
   * @param {HTMLElement} selectedOption
   */
  #updateAdaptivePickerSummary(selectedOption) {
    if (!(selectedOption instanceof HTMLInputElement)) return;

    const fieldset = selectedOption.closest('.variant-option--adaptive-compact');
    if (!(fieldset instanceof HTMLFieldSetElement)) return;

    const selectedLabel = selectedOption.dataset.optionDisplayLabel || selectedOption.value;
    const summaryValues = fieldset.querySelectorAll(
      '[data-key="ab-adaptive-summary-value"], [data-key="ab-adaptive-toggle-value"]'
    );
    for (const summaryValue of summaryValues) {
      summaryValue.textContent = selectedLabel;
    }

    const toggle = fieldset.querySelector('[data-adaptive-toggle]');
    if (toggle) {
      const optionName = fieldset.querySelector('.ab-adaptive-variant-heading')?.textContent || 'Style';
      toggle.setAttribute('aria-label', `${optionName}. Selected ${selectedLabel}. Change style.`);
    }

    const selectedMedia = fieldset.querySelector('[data-adaptive-selected-media]');
    const cardMedia = selectedOption.closest('.ab-adaptive-variant-card')?.querySelector('.ab-adaptive-variant-card__media');
    if (selectedMedia && cardMedia) {
      selectedMedia.replaceChildren(...Array.from(cardMedia.childNodes).map((node) => node.cloneNode(true)));
    }

    if (this.#adaptiveMediaQuery.matches) {
      this.#setAdaptivePickerExpanded(fieldset, false);
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    if (this.#isUnavailableCombination(selectedOption)) {
      this.#restoreSelectedOptionState(selectedOption);
      return;
    }

    this.updateSelectedOption(event.target);
    if (this.#eligibleVariants.length) {
      this.#syncSelectionToEligibleVariant(this.#preferredOptionFromSelectedOption(selectedOption));
      this.#applyVariantGate();
    }
    this.#updateAdaptivePickerSummary(selectedOption);
    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    const variantId = this.#selectedEligibleVariant()?.id || selectedOption.dataset.variantId || null;

    this.fetchUpdatedSection(
      this.buildRequestUrl(selectedOption, null, [], isOnProductPage ? variantId : null),
      morphElementSelector
    );

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @param {string | null} [variantId] - The selected variant ID.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = [], variantId = null) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);
    params.push(...getPreservedRequestParameters());

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    if (variantId) {
      params.push(`variant=${encodeURIComponent(variantId)}`);
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          newProduct = this.updateVariantPicker(html);
        }

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.#eligibleVariants = this.#readVariantGate();
    this.#collectFieldsetState();
    if (this.#eligibleVariants.length) {
      this.#syncSelectionToEligibleVariant();
      this.#applyVariantGate();
    }
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
